// useRunning (T-254, running indicator and ETA): polls GET /api/watchers/running and ticks a clock so the "time left" counts down between
// polls. Used by the header pill (components/RunningIndicator.jsx, every interface version) and the Watchers page banner.
// Polling pauses while the tab is hidden; a failed poll keeps the last good answer and flags `stale` so the UI never shows an old
// "IDLE" as if it were fresh (a wrong all-clear is the one thing this indicator must not give).
import { useCallback, useEffect, useState } from 'react'
import { watchers as api } from './api'

const STALE_AFTER_MS = 60_000   // no good answer for a minute -> the pill says so instead of showing the last state

export function useRunning(pollMs = 15_000) {
  const [state, setState] = useState({ data: null, fetchedAt: 0, error: null })
  const [now, setNow] = useState(() => Date.now())

  const load = useCallback(() => {
    api.running()
      .then(r => setState({ data: r && r.success ? r : null, fetchedAt: Date.now(), error: r && r.success ? null : (r && r.error) || 'failed' }))
      .catch(e => setState(prev => ({ ...prev, error: String((e && e.message) || e) })))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(() => { if (!document.hidden) load() }, pollMs)
    const onVisible = () => { if (!document.hidden) load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible) }
  }, [load, pollMs])

  const ticking = !!(state.data && state.data.items && state.data.items.length)
  useEffect(() => {
    if (!ticking) return undefined
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [ticking])

  const clock = ticking ? now : Date.now()
  const stale = !!state.error || (state.fetchedAt > 0 && clock - state.fetchedAt > STALE_AFTER_MS)
  return { data: state.data, fetchedAt: state.fetchedAt || clock, now: clock, error: state.error, stale, reload: load }
}
